import { TEST_CONSTANTS } from '../constants';
import { ROUTES } from '../routes';
import {
  AdaptiveQuestion,
  AdaptiveQuestionResponse,
  AdaptiveVocabularyTest,
} from '../utils/adaptiveModels';
import { getUserProfileFromLocalStorage } from './userProfile';

const ADAPTIVE_TEST_STORAGE_KEY = 'adaptiveVocabTest';
const ADAPTIVE_TEST_PROGRESS_KEY = 'adaptiveVocabTestProgress';
const ADAPTIVE_TEST_ANSWERS_KEY = 'adaptiveVocabTestAnswers';
const ADAPTIVE_COMPLETED_TESTS_KEY = 'adaptiveCompletedVocabTests';
const ADAPTIVE_RETRY_PENDING_KEY = 'adaptiveVocabTestRetryPending';
let adaptiveTestStorageReset = false;

// A stored vocab test from another frontend version is dropped and a new one
// is created by the App-level effect.
function isCompatibleAdaptiveTest(test: AdaptiveVocabularyTest): boolean {
  return test.test_version === TEST_CONSTANTS.ADAPTIVE_VERSION;
}

function resetAdaptiveTestStorage(): void {
  adaptiveTestStorageReset = true;
  localStorage.removeItem(ADAPTIVE_TEST_STORAGE_KEY);
  localStorage.removeItem(ADAPTIVE_TEST_PROGRESS_KEY);
  localStorage.removeItem(ADAPTIVE_TEST_ANSWERS_KEY);
  console.debug('Cleared incompatible adaptive vocab test from localStorage');
}

export function consumeAdaptiveTestStorageReset(): boolean {
  const result = adaptiveTestStorageReset;
  adaptiveTestStorageReset = false;
  return result;
}

export function getAdaptiveTestFromLocalStorage(): AdaptiveVocabularyTest | null {
  const testData = localStorage.getItem(ADAPTIVE_TEST_STORAGE_KEY);

  if (!testData) {
    return null;
  }

  const userProfile = getUserProfileFromLocalStorage();

  try {
    const parsedTest = JSON.parse(testData) as AdaptiveVocabularyTest;

    if (!isCompatibleAdaptiveTest(parsedTest)) {
      resetAdaptiveTestStorage();
      return null;
    }

    if (userProfile) {
      parsedTest.user_profile = userProfile;
    }

    return parsedTest;
  } catch (error) {
    console.error('Failed to parse adaptive vocab test from localStorage:', error);
    resetAdaptiveTestStorage();
    return null;
  }
}

export function saveAdaptiveTestToLocalStorage(test: AdaptiveVocabularyTest): void {
  try {
    localStorage.setItem(ADAPTIVE_TEST_STORAGE_KEY, JSON.stringify(test));
    console.debug('Saved adaptive vocab test to localStorage', test);
  } catch (error) {
    console.error('Failed to save adaptive vocab test to localStorage:', error);
  }
}

export function clearAdaptiveTestFromLocalStorage(): void {
  localStorage.removeItem(ADAPTIVE_TEST_STORAGE_KEY);
  localStorage.removeItem(ADAPTIVE_TEST_PROGRESS_KEY);
  localStorage.removeItem(ADAPTIVE_TEST_ANSWERS_KEY);
  console.debug('Cleared adaptive vocab test from localStorage');
}

export function markAdaptiveTestInProgress(): void {
  const test = getAdaptiveTestFromLocalStorage();
  if (!test || test.status !== 'new') return;
  saveAdaptiveTestToLocalStorage({ ...test, status: 'in-progress' });
}

// --- Progress (owned by the backend, mirrored for the home page's resume label
// and the results screen's completeness check) ---

interface AdaptiveTestProgress {
  test_id: string;
  // Answered self-reporting pages over the total, in [0, 1].
  progress: number;
  // The backend served an empty page: the vocab test is over and can be completed.
  finished: boolean;
}

export function saveAdaptiveTestProgress(progress: AdaptiveTestProgress): void {
  try {
    localStorage.setItem(ADAPTIVE_TEST_PROGRESS_KEY, JSON.stringify(progress));
  } catch (error) {
    console.error('Failed to save adaptive vocab test progress to localStorage:', error);
  }
}

export function getAdaptiveTestProgress(): AdaptiveTestProgress | null {
  const test = getAdaptiveTestFromLocalStorage();
  const raw = localStorage.getItem(ADAPTIVE_TEST_PROGRESS_KEY);
  if (!test || !raw) return null;
  try {
    const progress = JSON.parse(raw) as AdaptiveTestProgress;
    return progress.test_id === test.test_id ? progress : null;
  } catch (error) {
    console.error('Failed to parse adaptive vocab test progress from localStorage:', error);
    return null;
  }
}

// --- Answered self-reporting pages ---
//
// Kept only for the results funnel's local fallback (`deriveUnknownWords`)
// when `GET /{test_id}/concepts/` fails. Headword-translation pages are not
// stored: they are verification-only and carry no known/unknown claim.

interface AdaptiveTestAnswers {
  test_id: string;
  questions: AdaptiveQuestion[];
  responses: AdaptiveQuestionResponse[];
}

export function getAnsweredSelfReportingQuestions(testId: string): AdaptiveTestAnswers {
  const empty: AdaptiveTestAnswers = { test_id: testId, questions: [], responses: [] };
  const raw = localStorage.getItem(ADAPTIVE_TEST_ANSWERS_KEY);
  if (!raw) return empty;
  try {
    const answers = JSON.parse(raw) as AdaptiveTestAnswers;
    return answers.test_id === testId ? answers : empty;
  } catch (error) {
    console.error('Failed to parse adaptive vocab test answers from localStorage:', error);
    return empty;
  }
}

export function appendAnsweredSelfReportingPage(
  testId: string,
  questions: AdaptiveQuestion[],
  responses: AdaptiveQuestionResponse[],
): void {
  const answers = getAnsweredSelfReportingQuestions(testId);
  const knownIndexes = new Set(answers.questions.map((question) => question.in_test_index));
  const merged: AdaptiveTestAnswers = {
    test_id: testId,
    questions: [
      ...answers.questions,
      ...questions.filter(
        (question) => question.self_reporting != null && !knownIndexes.has(question.in_test_index),
      ),
    ],
    responses: [
      ...answers.responses,
      ...responses.filter(
        (response) => response.self_reporting != null && !knownIndexes.has(response.in_test_index),
      ),
    ],
  };
  try {
    localStorage.setItem(ADAPTIVE_TEST_ANSWERS_KEY, JSON.stringify(merged));
  } catch (error) {
    console.error('Failed to save adaptive vocab test answers to localStorage:', error);
  }
}

export function shouldFetchNewAdaptiveTest(): boolean {
  const existingTest = getAdaptiveTestFromLocalStorage();

  // pathname includes the deploy basename; BASE_URL ends with "/", so strip the
  // route's leading slash before joining.
  if (
    window.location.pathname.startsWith(
      `${import.meta.env.BASE_URL}${ROUTES.RESULT_PREFIX.slice(1)}`,
    )
  ) {
    console.debug('Skipping new test fetch due to result page');
    return false;
  }

  const result = !existingTest || existingTest.status === 'completed';
  console.debug('Checking if new adaptive test should be fetched:', result);
  return result;
}

// The retry-pending flag is set when the backend discards a finished test as
// unreliable. It survives tab closes.
export function setAdaptiveTestRetryPending(): void {
  localStorage.setItem(ADAPTIVE_RETRY_PENDING_KEY, 'true');
}

export function clearAdaptiveTestRetryPending(): void {
  localStorage.removeItem(ADAPTIVE_RETRY_PENDING_KEY);
}

export function isAdaptiveTestRetryPending(): boolean {
  return localStorage.getItem(ADAPTIVE_RETRY_PENDING_KEY) === 'true';
}

interface CompletedAdaptiveTest {
  timestamp: string;
  shortcode: string;
}

export function saveAdaptiveCompletedTestToHistory(test: AdaptiveVocabularyTest): void {
  if (!test.shortcode) return;

  let completedTests = getAdaptiveCompletedTestsHistory();
  if (completedTests.some((t) => t.shortcode === test.shortcode)) {
    console.debug(
      `Adaptive test with shortcode ${test.shortcode} already exists in history, skipping save.`,
    );
    return;
  }

  completedTests = [
    ...completedTests,
    {
      timestamp: new Date().toISOString(),
      shortcode: test.shortcode,
    },
  ];

  completedTests = completedTests
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, 10);

  localStorage.setItem(ADAPTIVE_COMPLETED_TESTS_KEY, JSON.stringify(completedTests));
  console.debug(`Saved adaptive completed test to history: ${test.shortcode}`);
}

export function getAdaptiveLastCompletedTestShortcode(): string | null {
  const completedTests = getAdaptiveCompletedTestsHistory();

  if (completedTests.length === 0) {
    return null;
  }

  return completedTests[0].shortcode;
}

export function getAdaptiveCompletedTestsHistory(): CompletedAdaptiveTest[] {
  const completedTests = JSON.parse(
    localStorage.getItem(ADAPTIVE_COMPLETED_TESTS_KEY) || '[]',
  ) as CompletedAdaptiveTest[];

  return completedTests.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
  );
}
